import { AccreditationStatus, ZoneType } from "../types/media";

// ============================================================================
// Pure helpers for the Demandes table (kept out of the hook module so the
// node vitest runner can import them without the Supabase client).
// ============================================================================

export interface DemandRequestCounts {
    /** Every child accreditation_requests row of the demand. */
    total: number;
    approved: number;
    /** rejected + revoked — both are "refused" from the demand's point of view. */
    rejected: number;
    /** Children still awaiting a decision (submitted / under_review). */
    reviewable: number;
    /** total − approved − rejected (reviewable + drafts/expired). */
    pending: number;
}

/**
 * Per-demand child status counts. `reviewable` mirrors what
 * review-accreditation-demand actually acts on (submitted / under_review),
 * so it is the eligibility test for the bulk actions.
 */
export function demandRequestCounts(
    requests: Array<{ status: AccreditationStatus | string }> | null | undefined
): DemandRequestCounts {
    const rows = requests ?? [];
    let approved = 0;
    let rejected = 0;
    let reviewable = 0;
    for (const row of rows) {
        if (row.status === AccreditationStatus.APPROVED) approved += 1;
        else if (
            row.status === AccreditationStatus.REJECTED ||
            row.status === AccreditationStatus.REVOKED
        )
            rejected += 1;
        if (
            row.status === AccreditationStatus.SUBMITTED ||
            row.status === AccreditationStatus.UNDER_REVIEW
        )
            reviewable += 1;
    }
    return {
        total: rows.length,
        approved,
        rejected,
        reviewable,
        pending: rows.length - approved - rejected,
    };
}

/**
 * Kick-off instant of a demand's match. `matches.match_date` is a DATE and
 * `matches.match_time` a TIME, so they are recombined into a local-time ISO
 * string usable by `cutoffState()` and by date sorting.
 * Returns null when there is no match date.
 */
export function demandKickoffIso(
    match: { match_date?: string | null; match_time?: string | null } | null | undefined
): string | null {
    const date = match?.match_date;
    if (!date) return null;
    const rawTime = match?.match_time?.trim();
    const time = rawTime && rawTime.length >= 4 ? rawTime : "00:00:00";
    return `${date.slice(0, 10)}T${time.length === 5 ? `${time}:00` : time}`;
}

/** Algeria is UTC+01:00 year-round (utils/algiersTime.ts). */
const ALGIERS_OFFSET = "+01:00";
const DAY_MS = 86_400_000;

/**
 * Kick-off instant (epoch ms) of a match. `match_date` + `match_time` are
 * Algiers wall time, so the offset is explicit — never the browser's zone.
 * Without a kick-off time the whole match day counts: the instant returned is
 * the end of that Algiers day (next day 00:00), so a time-less match only
 * becomes "past" once its day is over.
 * Returns null without a (parsable) match date.
 */
export function demandKickoffMs(
    match: { match_date?: string | null; match_time?: string | null } | null | undefined
): number | null {
    const iso = demandKickoffIso(match);
    if (!iso) return null;
    const ms = new Date(`${iso}${ALGIERS_OFFSET}`).getTime();
    if (Number.isNaN(ms)) return null;
    const hasTime = (match?.match_time?.trim().length ?? 0) >= 4;
    return hasTime ? ms : ms + DAY_MS;
}

/**
 * A demand still `pending` whose kick-off has passed: nothing can be done for
 * it any more (live example: Journal Kawalisse, 26/09, zero journalists). It
 * is shown « Match passé », left out of every pending counter and sorted after
 * the actionable demands.
 */
export function isStalePendingDemand(
    demand: {
        status: string;
        match?: { match_date?: string | null; match_time?: string | null } | null;
    },
    now: Date
): boolean {
    if (demand.status !== "pending") return false;
    const kickoff = demandKickoffMs(demand.match);
    return kickoff !== null && kickoff <= now.getTime();
}

/** Derived display status of a pending demand whose kick-off has passed. */
export const MATCH_PASSED_STATUS = "match_passed";

/** Status shown for a demand: its DB status, or the derived `match_passed`. */
export function demandDisplayStatus(
    demand: Parameters<typeof isStalePendingDemand>[0],
    now: Date
): string {
    return isStalePendingDemand(demand, now) ? MATCH_PASSED_STATUS : demand.status;
}

/** Pending AND kick-off still ahead (or unknown): a reviewer can act on it. */
export function isActionableDemand(
    demand: Parameters<typeof isStalePendingDemand>[0],
    now: Date
): boolean {
    return demand.status === "pending" && !isStalePendingDemand(demand, now);
}

/** The « Demandes en attente » count every surface must agree on. */
export function countActionableDemands(
    demands: ReadonlyArray<Parameters<typeof isStalePendingDemand>[0]> | null | undefined,
    now: Date
): number {
    return (demands ?? []).filter((d) => isActionableDemand(d, now)).length;
}

/**
 * Urgency rank for the default sort: 0 actionable pending, 1 pending with a
 * past kick-off, 2 decided. Ties are broken by kick-off ascending.
 */
export function demandUrgencyRank(
    demand: Parameters<typeof isStalePendingDemand>[0],
    now: Date
): 0 | 1 | 2 {
    if (demand.status !== "pending") return 2;
    return isStalePendingDemand(demand, now) ? 1 : 0;
}

// ============================================================================
// Decision attribution (who decided, and when)
// ============================================================================

/** Shown in place of a decider name that could not be resolved. */
export const DECIDER_FALLBACK = "—";

export interface DemandDecision {
    /** Resolved reviewer display name, or `DECIDER_FALLBACK` when unknown. */
    deciderName: string;
    /** Decision timestamp, or null when the row carries no `reviewed_at`. */
    reviewedAt: string | null;
}

/**
 * Decision attribution of one demand, or null while the demand is still
 * pending — nothing must be rendered then.
 *
 * `accreditation_demands.reviewed_by` references `auth.users(id)`, so PostgREST
 * has no embed path: the name is resolved by a best-effort secondary select in
 * `useAccreditationDemands` and lands on `reviewer_name`. A failed or missing
 * lookup degrades to `DECIDER_FALLBACK` rather than hiding the attribution.
 */
export function demandDecision(demand: {
    reviewed_by?: string | null;
    reviewed_at?: string | null;
    reviewer_name?: string | null;
}): DemandDecision | null {
    if (!demand.reviewed_at && !demand.reviewed_by) return null;
    return {
        deciderName: demand.reviewer_name?.trim() || DECIDER_FALLBACK,
        reviewedAt: demand.reviewed_at ?? null,
    };
}

// ============================================================================
// Per-match cap and per-journalist zones (org-only model)
// ============================================================================

/** Active accreditations an organization may hold per match (media_match_org_cap()). */
export const MATCH_ORG_CAP = 2;

/** Statuses that hold a slot of the cap; rejected / revoked / cancelled free it. */
export const ACTIVE_REQUEST_STATUSES: readonly string[] = [
    "draft",
    AccreditationStatus.SUBMITTED,
    AccreditationStatus.UNDER_REVIEW,
    AccreditationStatus.APPROVED,
];

export interface PerMatchQuota {
    active: number;
    cap: number;
    /** No slot left for this (organization, match). */
    full: boolean;
    /** Legacy data above the cap (created before the DB trigger). */
    over: boolean;
}

/** "x / 2" for one (organization, match): every request of the org on the match. */
export function perMatchQuota(
    rows: Array<{ status: string }> | null | undefined,
    cap: number = MATCH_ORG_CAP
): PerMatchQuota {
    const active = (rows ?? []).filter((r) =>
        ACTIVE_REQUEST_STATUSES.includes(r.status)
    ).length;
    return { active, cap, full: active >= cap, over: active > cap };
}

const ZONE_VALUES = new Set<string>(Object.values(ZoneType));
const PITCH_MEDIA_TYPES = new Set(["photographer", "cameraman", "tv_crew"]);

/**
 * Zones pre-selected for one journalist of a demand: what the organization
 * requested, else the media-type default used by submit-accreditation-demand.
 */
export function initialRowZones(row: {
    requested_zones?: readonly string[] | null;
    media_personnel?: { media_type?: string | null } | null;
}): ZoneType[] {
    const requested = (row.requested_zones ?? []).filter((z) =>
        ZONE_VALUES.has(z)
    ) as ZoneType[];
    if (requested.length) return [...new Set(requested)];
    return PITCH_MEDIA_TYPES.has(row.media_personnel?.media_type ?? "")
        ? [ZoneType.PITCHSIDE]
        : [ZoneType.PRESS_TRIBUNE];
}

export type DemandRowAction = "approve" | "reject";

export interface DemandReviewOverride {
    action: DemandRowAction;
    approved_zones?: ZoneType[];
}

/**
 * `overrides` for review-accreditation-demand: one entry per reviewable
 * journalist, carrying its resolved action and — when approved — its own
 * zones. `missingZones` lists the approved journalists left without a zone
 * (the function would answer INVALID_ZONES).
 */
export function buildReviewOverrides(input: {
    reviewableIds: readonly string[];
    defaultAction: DemandRowAction;
    actions: Record<string, DemandRowAction>;
    zones: Record<string, readonly ZoneType[]>;
}): { overrides: Record<string, DemandReviewOverride>; missingZones: string[] } {
    const overrides: Record<string, DemandReviewOverride> = {};
    const missingZones: string[] = [];
    for (const id of input.reviewableIds) {
        const action = input.actions[id] ?? input.defaultAction;
        if (action === "reject") {
            overrides[id] = { action };
            continue;
        }
        const zones = [...(input.zones[id] ?? [])];
        if (zones.length === 0) missingZones.push(id);
        overrides[id] = { action, approved_zones: zones };
    }
    return { overrides, missingZones };
}
